/**
 * Abaco Brands — Teacher Toolkit (Parts 3, 4, 5, 7)
 *
 * Shared logic used by both the hero form and the popup form
 * ([data-ttk-form]). Responsibilities:
 *   1. Validate + score the teacher email address (educational domain check).
 *   2. Create/update the Shopify Customer using Shopify's native, tokenless
 *      "customer" contact-form endpoint (no backend required — Path A).
 *   3. Best-effort POST the full lead (consent, source, timestamp, score) to
 *      an App Proxy endpoint for a backend (Path B) to do Admin-API tagging,
 *      ESP sync (Klaviyo/Mailchimp/Omnisend), and the delivery email. If no
 *      backend is deployed this call simply fails silently and Path A (native
 *      form + Shopify Flow, see /docs) still completes the signup.
 *   4. Redirect to the Thank You page on success.
 *
 * Configure via a global before this script loads, e.g. in theme.liquid:
 *   <script>
 *     window.TeacherToolkitConfig = {
 *       proxyEndpoint: '/apps/toolkit/subscribe' // omit if Path A only
 *     };
 *   </script>
 */
(function () {
  'use strict';

  var CONFIG = window.TeacherToolkitConfig || {};
  var PROXY_ENDPOINT = CONFIG.proxyEndpoint || '/apps/toolkit/subscribe';
  var STORAGE_KEY = 'ttk_converted'; // localStorage flag: this visitor already downloaded

  // --------------------------------------------------------------------
  // Email validation + educational-domain scoring (Part 3)
  // --------------------------------------------------------------------

  // Deliberately simple, robust regex — good enough to catch typos without
  // rejecting valid-but-unusual addresses. Real deliverability is confirmed
  // by the email actually being sendable, not by regex perfection.
  var EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

  // Substrings found in K-12 / district / school email domains.
  // Intentionally broad and case-insensitive; matches on the domain only.
  var EDU_KEYWORDS = [
    '.edu', 'k12', 'k-12', '.k12.', 'schools', 'school', 'isd', 'usd',
    'district', 'academy', 'college', 'university', 'elementary',
    'middleschool', 'highschool', '.sch.', '.org.edu'
  ];

  // Common personal providers — NEVER blocked, just informative.
  var PERSONAL_PROVIDERS = [
    'gmail.com', 'yahoo.com', 'outlook.com', 'hotmail.com', 'icloud.com',
    'aol.com', 'live.com', 'msn.com', 'protonmail.com'
  ];

  // Known disposable/temporary-inbox domains — these are rejected outright.
  // This is a format/pattern check, not real deliverability verification: a
  // syntactically valid but fake domain (e.g. a made-up address) will still
  // get through. Catching that requires MX/mailbox verification, which needs
  // a server (Path B) or a third-party ESP with double opt-in.
  var DISPOSABLE_DOMAINS = [
    'mailinator.com', 'mailinator.net', 'mailinator.org', 'guerrillamail.com',
    'guerrillamail.info', 'guerrillamail.biz', 'guerrillamail.de',
    'guerrillamailblock.com', 'sharklasers.com', 'grr.la', 'pokemail.net',
    '10minutemail.com', '10minutemail.net', '20minutemail.com',
    'tempmail.com', 'temp-mail.org', 'temp-mail.io', 'tempinbox.com',
    'tempr.email', 'tmpmail.org', 'tmpmail.net', 'tmpeml.com',
    'yopmail.com', 'yopmail.net', 'yopmail.fr', 'cool.fr.nf',
    'throwawaymail.com', 'getnada.com', 'nada.email', 'trashmail.com',
    'trashmail.me', 'dispostable.com', 'fakeinbox.com', 'fakemailgenerator.com',
    'maildrop.cc', 'mintemail.com', 'mohmal.com', 'moakt.cc', 'moakt.com',
    'spamgourmet.com', 'mailnesia.com', 'mailcatch.com', 'emailondeck.com',
    'discard.email', 'discardmail.com', 'spambog.com', 'mytemp.email',
    'inboxbear.com', 'mailsac.com', 'mailtemp.info', 'burnermail.io',
    'byom.de', 'einrot.com', 'jetable.org', 'mytrashmail.com'
  ];

  function isValidEmailFormat(email) {
    return EMAIL_REGEX.test(String(email || '').trim());
  }

  function isDisposableEmail(email) {
    var domain = String(email || '').split('@')[1] || '';
    return DISPOSABLE_DOMAINS.indexOf(domain.toLowerCase()) !== -1;
  }

  /**
   * Scores an email's domain for educational likelihood.
   * Returns { verified: boolean, matchedKeyword: string|null, domain: string, isPersonalProvider: boolean }
   * Never rejects the email — this is a tag, not a gate.
   */
  function scoreEmailDomain(email) {
    var domain = String(email || '').split('@')[1] || '';
    domain = domain.toLowerCase();

    var isPersonalProvider = PERSONAL_PROVIDERS.indexOf(domain) !== -1;
    var matchedKeyword = null;

    for (var i = 0; i < EDU_KEYWORDS.length; i++) {
      if (domain.indexOf(EDU_KEYWORDS[i]) !== -1) {
        matchedKeyword = EDU_KEYWORDS[i];
        break;
      }
    }

    return {
      verified: !!matchedKeyword,
      matchedKeyword: matchedKeyword,
      domain: domain,
      isPersonalProvider: isPersonalProvider
    };
  }

  // --------------------------------------------------------------------
  // Shopify native customer creation (Path A — no backend, no API token)
  // --------------------------------------------------------------------

  /**
   * Uses Shopify's built-in "/contact" customer form handler. This is the
   * same mechanism that powers footer newsletter signups: it creates the
   * customer if new, or updates them if they already exist, and sets email
   * marketing consent — all without any Admin API token. It does NOT
   * reliably support setting tags (Shopify's contact-form handler does not
   * document a tags field), so tagging is handled by Shopify Flow (Path A)
   * or by the App Proxy backend (Path B), not here.
   */
  function submitNativeCustomerForm(email, acceptsMarketing) {
    var body = new URLSearchParams();
    body.set('form_type', 'customer');
    body.set('utf8', '✓');
    body.set('contact[email]', email);
    body.set('contact[accepts_marketing]', acceptsMarketing ? '1' : '0');

    return fetch('/contact#newsletter', {
      method: 'POST',
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      body: body.toString(),
      credentials: 'same-origin'
    });
  }

  /**
   * Best-effort call to the App Proxy backend (Path B). If no backend is
   * deployed this 404s or the fetch throws — both are swallowed so Path A
   * still completes the signup on its own.
   */
  function submitProxyEndpoint(payload) {
    return fetch(PROXY_ENDPOINT, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
      credentials: 'same-origin'
    })
      .then(function (res) {
        if (!res.ok) return null;
        return res.json().catch(function () { return null; });
      })
      .catch(function () {
        return null; // no backend installed — Path A already handled it
      });
  }

  // --------------------------------------------------------------------
  // Form wiring
  // --------------------------------------------------------------------

  function setFieldError(input, message) {
    var errorEl = document.getElementById(input.getAttribute('aria-describedby'));
    if (message) {
      input.setAttribute('aria-invalid', 'true');
      if (errorEl) {
        errorEl.textContent = message;
        errorEl.hidden = false;
      }
    } else {
      input.removeAttribute('aria-invalid');
      if (errorEl) {
        errorEl.textContent = '';
        errorEl.hidden = true;
      }
    }
  }

  function setMessage(form, state, text) {
    var el = form.querySelector('[data-ttk-message]');
    if (!el) return;
    el.textContent = text;
    el.dataset.state = state;
    el.hidden = !text;
  }

  function setLoading(form, isLoading) {
    var btn = form.querySelector('[data-ttk-submit]');
    if (!btn) return;
    var spinner = btn.querySelector('.ttk-button__spinner');
    btn.disabled = isLoading;
    if (spinner) spinner.hidden = !isLoading;
  }

  function markConverted() {
    try {
      localStorage.setItem(STORAGE_KEY, '1');
      document.dispatchEvent(new CustomEvent('ttk:converted'));
    } catch (e) {
      /* localStorage unavailable (private mode) — non-fatal */
    }
  }

  function handleSubmit(event) {
    event.preventDefault();
    var form = event.currentTarget;
    var nameInput = form.querySelector('[data-ttk-name-input]');
    var emailInput = form.querySelector('[data-ttk-email-input]');
    var consentInput = form.querySelector('[data-ttk-consent-input]');
    var name = nameInput ? (nameInput.value || '').trim() : '';
    var email = (emailInput.value || '').trim();

    if (nameInput) setFieldError(nameInput, null);
    setFieldError(emailInput, null);
    setMessage(form, null, '');

    if (nameInput && nameInput.hasAttribute('required') && !name) {
      setFieldError(nameInput, 'Please enter your first name.');
      nameInput.focus();
      return;
    }

    if (!isValidEmailFormat(email)) {
      setFieldError(emailInput, 'Please enter a valid email address.');
      emailInput.focus();
      return;
    }

    if (isDisposableEmail(email)) {
      setFieldError(emailInput, 'Please use your school or personal email address — temporary/disposable emails aren’t accepted.');
      emailInput.focus();
      return;
    }

    if (consentInput && !consentInput.checked) {
      setMessage(form, 'error', 'Please check the consent box to continue.');
      consentInput.focus();
      return;
    }

    var score = scoreEmailDomain(email);
    var source = form.getAttribute('data-ttk-source') || 'Homepage Toolkit';
    var redirectUrl = form.getAttribute('data-ttk-redirect') || '/pages/thank-you';
    var consentText = (form.querySelector('.ttk-consent__label') || {}).textContent || '';
    var timestamp = new Date().toISOString();

    var tags = ['Teacher Lead', 'Toolkit Download'];
    tags.push(score.verified ? 'Teacher Verified' : 'Teacher Unverified');
    tags.push('Date Captured: ' + timestamp.slice(0, 10));

    var payload = {
      first_name: name, // not saved by the native Shopify form (email-only); forwarded here for a Path B backend if one is ever deployed
      email: email,
      consent: true,
      consent_text: consentText.trim(),
      consent_timestamp: timestamp,
      source: source, // "Homepage Toolkit" | "Popup Toolkit"
      page_url: window.location.href,
      teacher_verified: score.verified,
      matched_keyword: score.matchedKeyword,
      domain: score.domain,
      is_personal_provider: score.isPersonalProvider,
      tags: tags
    };

    setLoading(form, true);

    // Path A (always runs): native, tokenless customer create/update.
    // Path B (best-effort): richer backend for tags/ESP/consent metafields.
    Promise.allSettled([
      submitNativeCustomerForm(email, true),
      submitProxyEndpoint(payload)
    ]).then(function (results) {
      setLoading(form, false);
      var nativeResult = results[0];
      var proxyResult = results[1];

      var nativeOk = nativeResult.status === 'fulfilled';
      if (!nativeOk) {
        setMessage(form, 'error', "Something went wrong. Please try again in a moment.");
        return;
      }

      markConverted();

      // If the backend returned a verification token or direct download
      // URLs, forward them to the thank-you page as query params so it can
      // hard-verify/show them immediately (see teacher-toolkit-thank-you.js).
      var proxyData = proxyResult.status === 'fulfilled' ? proxyResult.value : null;
      var url = new URL(redirectUrl, window.location.origin);
      url.searchParams.set('source', source);
      url.searchParams.set('verified', score.verified ? '1' : '0');
      // Cosmetic only — not persisted to Shopify. Powers the "Thanks, {name}!"
      // greeting on the thank-you page (see teacher-toolkit-thank-you.js).
      if (name) {
        url.searchParams.set('name', name);
      }
      if (proxyData && proxyData.token) {
        url.searchParams.set('t', proxyData.token);
      }

      window.location.href = url.toString();
    });
  }

  function init() {
    var forms = document.querySelectorAll('[data-ttk-form]');
    forms.forEach(function (form) {
      form.addEventListener('submit', handleSubmit);

      var emailInput = form.querySelector('[data-ttk-email-input]');
      if (emailInput) {
        emailInput.addEventListener('input', function () {
          setFieldError(emailInput, null);
        });
      }

      var nameInput = form.querySelector('[data-ttk-name-input]');
      if (nameInput) {
        nameInput.addEventListener('input', function () {
          setFieldError(nameInput, null);
        });
      }
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Expose for the popup script and for debugging/testing.
  window.TeacherToolkit = {
    isValidEmailFormat: isValidEmailFormat,
    isDisposableEmail: isDisposableEmail,
    scoreEmailDomain: scoreEmailDomain,
    STORAGE_KEY: STORAGE_KEY
  };
})();
